import {
    confirmationDialogOptions,
    THIS_MESSAGE_OPTION,
    type confirmationDialogOption,
} from '../../recurring-dialog-options';
import type { CalendarLeanEvent } from '../types';
import { isResolvedSendChip } from './drag-model';

/**
 * Deleting ONE occurrence, chosen on the calendar.
 *
 * The engine has been able to do this for a long time — `handleDeletion` takes
 * an arbitrary occurrence instant and answers all three scopes — but nothing
 * could ASK it for a specific one. The only delete entry point was the sidebar
 * row, which knows a document and therefore only ever its `dueDate`: the
 * series' next occurrence. Deleting the third of five, or the last, was
 * unreachable rather than untested.
 *
 * A chip is the missing thing: a chip IS one occurrence, and it already carries
 * that occurrence's own instant. This module turns a chip into the request the
 * delete thunk already understands, and decides what question the customer is
 * asked before anything is written.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DO: convert the instant. The instant a
 * chip carries is not always the instant the rule emits (they part company
 * across a DST change), and an EXDATE written in the wrong time base makes the
 * occurrence GHOST — it vanishes and returns on the next expansion. That
 * conversion is `handleDeletion`'s (`toRuleTimeBase`), where it is already
 * correct and already tested. Solving it a second time here is how the two
 * copies would drift.
 */

export type OccurrenceDeletePlan =
    /** Not a message document — a campaign or sequence step. Not ours to delete. */
    | { kind: 'none' }
    /** One send, one act. No scope question: there is nothing else it could mean. */
    | { kind: 'single' }
    /** A recurring occurrence: the customer chooses how much of the series goes. */
    | { kind: 'scoped'; options: confirmationDialogOption[]; defaultIndex: number }
    /** #592b — a FUTURE occurrence of a recurring DRIP. Not deletable (it is
     *  synthetic, no document), but skippable per-date: the BE writes an EXDATE
     *  onto the drip's schedule and the series advances past it. #592c adds
     *  `stepIndex` (the chip's own step), so the same plan feeds BOTH the
     *  whole-cycle skip and the single-message ("this step") skip the chip menu
     *  offers. */
    | { kind: 'skip-drip-date'; dripId: string; occurrenceStartIso: string; stepIndex: number };

/**
 * #592b — a chip for a FUTURE occurrence of a recurring DRIP. Distinguished from
 * a materialized/imminent drip campaign chip by `dripOccurrenceStartMs`, which is
 * stamped ONLY on the synthetic `:occ:` occurrences (WhatsAppSelectors). Those
 * imminent campaign chips keep their existing (delete-less) behaviour; the
 * imminent date is skipped via the drip view's "skip next" action instead.
 */
function isRecurringDripOccurrence(
    event: Pick<CalendarLeanEvent, 'isCampaign' | 'isRecurring' | 'dripId' | 'dripOccurrenceStartMs'>,
): boolean {
    return !!event.isCampaign && !!event.isRecurring && !!event.dripId && event.dripOccurrenceStartMs != null;
}

export interface OccurrenceDeleteRequest {
    value: confirmationDialogOption;
    event: {
        _id: string;
        /** The CHIP's instant — see the note above about time bases. */
        dueDate: string;
        isCampaign: false;
        isRecurring: true;
    };
}

/** Whether this occurrence belongs to a series that can actually be sliced. */
function isSliceableSeries(event: Pick<CalendarLeanEvent, 'isCampaign' | 'isRecurring' | 'rruleset'>): boolean {
    return !event.isCampaign && !!event.isRecurring && !!event.rruleset;
}

/**
 * What to ask before deleting the occurrence this chip stands for.
 *
 * A NON-recurring send gets no scope question at all. "Only this one" and "the
 * whole series" describe the same act when there is one send, and offering both
 * asks the customer to choose between two identical outcomes — which reads as
 * though one of them must be worse.
 *
 * A campaign or sequence step gets no delete here: it is not a message
 * document, deleting one means something else entirely (it belongs to a
 * campaign's own lifecycle), and it already has its own surface.
 */
export function planOccurrenceDelete(
    event: Pick<CalendarLeanEvent, 'isCampaign' | 'isRecurring' | 'rruleset'> & Partial<Pick<CalendarLeanEvent, 'effectiveStatus' | 'campaignStatus' | 'isSentRecord' | 'dripId' | 'dripOccurrenceStartMs' | 'dripStepIndex' | 'dueDate'>> | null | undefined,
): OccurrenceDeletePlan {
    if (!event) return { kind: 'none' };
    // #592b — a future recurring-DRIP occurrence: not deletable, but skippable
    // per-date. Checked BEFORE the `isCampaign` gate (drip occurrences are
    // campaigns), and scoped by `dripOccurrenceStartMs` so only the synthetic
    // future occurrences match — never a materialized campaign chip.
    if (isRecurringDripOccurrence(event as CalendarLeanEvent)) {
        return {
            kind: 'skip-drip-date',
            dripId: String(event.dripId),
            occurrenceStartIso: new Date(event.dripOccurrenceStartMs as number).toISOString(),
            // #592c — the chip's own step. The whole-cycle skip ignores it; the
            // single-message skip targets exactly this step of this occurrence.
            stepIndex: typeof event.dripStepIndex === 'number' ? event.dripStepIndex : 0,
        };
    }
    if (event.isCampaign) return { kind: 'none' };
    // A send that already went out is a record, not a plan: deleting it would erase the
    // history "Show sent" exists to show — and it would not un-send anything.
    if (isResolvedSendChip(event as CalendarLeanEvent)) return { kind: 'none' };
    if (!isSliceableSeries(event)) return { kind: 'single' };

    // Not a hard-coded 0: the guarantee is about WHICH option is pre-selected,
    // so a confirm pressed without reading removes one occurrence and never the
    // series, whatever order the list is later given.
    const defaultIndex = Math.max(0, confirmationDialogOptions.indexOf(THIS_MESSAGE_OPTION));
    return { kind: 'scoped', options: confirmationDialogOptions, defaultIndex };
}

/** The instant this chip stands for, as an ISO string. */
export function occurrenceInstantIso(event: Pick<CalendarLeanEvent, 'dueDate'>): string {
    return new Date(event.dueDate as any).toISOString();
}

/**
 * The payload `onDeleteRecurringDialogCloseThunk` already understands, built
 * around the chip's own occurrence.
 *
 * Throws rather than degrading: a wrong-shaped payload would reach the handler
 * and fail somewhere inside rrule, where the customer sees nothing at all.
 */
export function buildRecurringDeleteRequest(
    event: CalendarLeanEvent,
    value: confirmationDialogOption,
): OccurrenceDeleteRequest {
    if (!isSliceableSeries(event)) {
        throw new Error('buildRecurringDeleteRequest: not a recurring message occurrence');
    }
    return {
        value,
        event: {
            _id: event._id,
            dueDate: occurrenceInstantIso(event),
            isCampaign: false,
            isRecurring: true,
        },
    };
}

/**
 * What a closed confirmation dialog means.
 *
 * Extracted so the ONE invariant that matters here is testable rather than
 * asserted by reading a component: nothing is written until the customer says
 * so. `undefined` is how `MessageConfirmationDialog` reports Cancel, Escape and
 * a click outside, and all three must resolve to 'nothing' — the grid's handler
 * is then a switch with no judgement of its own left in it.
 */
export type DeleteConfirmation =
    | { kind: 'nothing' }
    | { kind: 'recurring'; request: OccurrenceDeleteRequest }
    | { kind: 'single'; _id: string }
    /** #592b — skip the WHOLE cycle on one date of a recurring drip (no deletion). */
    | { kind: 'skip-drip-date'; dripId: string; occurrenceStartIso: string }
    /** #592c — skip ONE step ("message") of a recurring drip's cycle, for all
     *  contacts, leaving the rest of the cycle and the series intact. */
    | { kind: 'skip-drip-step'; dripId: string; occurrenceStartIso: string; stepIndex: number }
    /** #592c — UNDO a prior single-step skip on this occurrence. Non-destructive,
     *  so the chip menu fires it without a confirm dialog. */
    | { kind: 'unskip-drip-step'; dripId: string; occurrenceStartIso: string; stepIndex: number };

export function resolveDeleteConfirmation(
    event: CalendarLeanEvent | null | undefined,
    value: string | undefined,
): DeleteConfirmation {
    if (!event) return { kind: 'nothing' };
    // Cancel / Escape / dismissed. The ONLY branch that reaches a dispatch is a
    // value the customer chose.
    if (value === undefined) return { kind: 'nothing' };

    const plan = planOccurrenceDelete(event);
    if (plan.kind === 'none') return { kind: 'nothing' };
    if (plan.kind === 'single') return { kind: 'single', _id: event._id };
    // #592b — a per-date drip skip has no scope question; any confirmed value
    // (the dialog's single "skip" button) resolves to the skip.
    if (plan.kind === 'skip-drip-date') {
        return { kind: 'skip-drip-date', dripId: plan.dripId, occurrenceStartIso: plan.occurrenceStartIso };
    }

    // A scoped delete with no scope chosen is not a delete. The dialog
    // pre-selects one, so this is unreachable through the UI — and it is exactly
    // the shape that, left unguarded, would fall through to 'thisAndFollowing'
    // in `scopeForOption` and take the rest of the series with it.
    if (!plan.options.includes(value as confirmationDialogOption)) return { kind: 'nothing' };

    return { kind: 'recurring', request: buildRecurringDeleteRequest(event, value as confirmationDialogOption) };
}

/**
 * #592c — what a closed single-STEP skip confirmation means.
 *
 * The chip menu's "Skip this message" opens its own confirm dialog; this maps a
 * closed dialog to the write. Same discipline as `resolveDeleteConfirmation`:
 * `undefined` (Cancel/Escape/dismiss) and a chip that is not a recurring-drip
 * occurrence both resolve to 'nothing', so nothing is written unless the customer
 * confirmed on an eligible chip. The step targeted is the chip's OWN step, at the
 * OCCURRENCE START instant (never the follow-up step's shifted dueDate — the BE
 * matches the override to the cycle's dueDate at minute granularity).
 */
export function resolveStepSkipConfirmation(
    event: CalendarLeanEvent | null | undefined,
    value: string | undefined,
): DeleteConfirmation {
    if (!event) return { kind: 'nothing' };
    if (value === undefined) return { kind: 'nothing' };
    const plan = planOccurrenceDelete(event);
    if (plan.kind !== 'skip-drip-date') return { kind: 'nothing' };
    return {
        kind: 'skip-drip-step',
        dripId: plan.dripId,
        occurrenceStartIso: plan.occurrenceStartIso,
        stepIndex: plan.stepIndex,
    };
}

/**
 * #592c — what "Undo skip" on a chip means.
 *
 * Undo is the reversible, non-destructive direction, so — unlike skip — the chip
 * menu fires it straight from the menu item with no confirm dialog. There is
 * therefore no dialog `value` to interpret: choosing the menu item IS the
 * decision. Same eligibility gate as the skip resolver (only a recurring-drip
 * occurrence chip resolves to a write), and it targets the chip's OWN step at the
 * OCCURRENCE START instant — the exact `{dripId, occurrenceStartIso, stepIndex}`
 * the skip wrote, so the BE matches and removes the same override.
 */
export function resolveStepUnskipConfirmation(
    event: CalendarLeanEvent | null | undefined,
): DeleteConfirmation {
    if (!event) return { kind: 'nothing' };
    const plan = planOccurrenceDelete(event);
    if (plan.kind !== 'skip-drip-date') return { kind: 'nothing' };
    return {
        kind: 'unskip-drip-step',
        dripId: plan.dripId,
        occurrenceStartIso: plan.occurrenceStartIso,
        stepIndex: plan.stepIndex,
    };
}

/**
 * #592c — is this chip's step already skipped for its occurrence?
 *
 * Drives whether the chip menu offers "Skip this message" or "Undo skip". Mirrors
 * EXACTLY the hide logic in WhatsAppSelectors (`getRecurringMessages`) so the two
 * never disagree: the CURRENT cycle's `skippedSteps` apply to the occurrence that
 * sits on the drip's own `dueDate`; a `pendingStepSkips` override applies to
 * whichever future occurrence its `occurrenceDate` matches. Both compared at
 * MINUTE resolution (the BE↔client contract) against the chip's occurrence START.
 *
 * Pure so it is unit-tested rather than asserted by reading the grid.
 */
export interface ChipStepSkipInput {
    /** The drip's current-cycle `skippedSteps` (step indices). */
    skippedSteps?: number[] | null;
    /** The drip's `pendingStepSkips` future-occurrence overrides. */
    pendingStepSkips?: Array<{ occurrenceDate: string | number | Date; stepIndex: number }> | null;
    /** The drip's current-cycle due date — `skippedSteps` key off the occurrence
     *  that lands on this instant. */
    dripDueDate?: string | number | Date | null;
    /** The chip's occurrence START instant, in ms (`dripOccurrenceStartMs`). */
    occurrenceStartMs?: number | null;
    /** The chip's own step index. */
    stepIndex: number;
}

export function isChipStepSkipped(input: ChipStepSkipInput): boolean {
    const { skippedSteps, pendingStepSkips, dripDueDate, occurrenceStartMs, stepIndex } = input;
    if (occurrenceStartMs == null) return false;
    const occMinute = Math.floor(occurrenceStartMs / 60000);
    const pending = Array.isArray(pendingStepSkips) ? pendingStepSkips : [];
    for (const ps of pending) {
        if (!ps || ps.stepIndex !== stepIndex) continue;
        if (Math.floor(new Date(ps.occurrenceDate).getTime() / 60000) === occMinute) return true;
    }
    const dueMinute = dripDueDate != null ? Math.floor(new Date(dripDueDate).getTime() / 60000) : null;
    if (dueMinute != null && occMinute === dueMinute) {
        const set = Array.isArray(skippedSteps) ? skippedSteps : [];
        if (set.includes(stepIndex)) return true;
    }
    return false;
}
